
import { Component, Prop, Provide, Mixins } from "vue-property-decorator";
import { mapState } from "vuex";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { RegexMatch } from "@/data/enums/router";

// Types
import { IProvider } from "@/models/providers";
import {
  IShopProductProviderData,
  IShopProductProviderMethods
} from "@/models/providers/products";
import { IState } from "@/store";
import { IProduct } from "@/models/products";
import { IBasket } from "@/models/baskets";
import { ICurrency } from "@/models/constants";
import { IAccount } from "@/models/accounts";

@Component({
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      }
    })
  }
})
export default class ShopProductProvider
  extends Mixins(RequestPromises, Context)
  implements IProvider {
  // Map State

  public product!: IProduct;
  public productScope!: string;

  // Props

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ default: "" })
  public accountId!: IAccount["id"];

  @Prop({ default: "" })
  public basketId!: IBasket["id"];

  @Prop({ required: true })
  public currencyId!: ICurrency["id"];

  @Prop({ required: false })
  public omitPromotions!: boolean;

  @Prop({ default: () => [] })
  public coupons!: string[];

  @Prop({ default: null })
  public basketProduct!: any;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  // Life-cycle methods

  public async created() {
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getProductData(this.isReloading);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      isBasketContext: true,
      clientApi: true,
      params: _.merge(
        {},
        { currency_id: this.currencyId },
        this.omitPromotions ? { omit_promotions: 1 } : {},
        this.coupons ? { promotions: this.coupons.join() } : {},
        this.basketId && this.basketId !== RegexMatch.NEW
          ? { basket_id: this.basketId }
          : {},
        this.basketProduct ? { basket_product_id: this.basketProduct.id } : {},
        this.isAdmin && this.accountId ? { account_id: this.accountId } : {}
      ),
      vm: this,
      ignoreStored
    });
  }

  // Provide

  @Provide("shopProductProviderData")
  public providerData(): IShopProductProviderData {
    return {
      product: this.product,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError
    };
  }

  @Provide("shopProductProviderMethods")
  public get providerMethods(): IShopProductProviderMethods {
    return {
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $shopProductData: this.providerData(),
        $shopProductMethods: this.providerMethods
      })
    );
  }
}
